import { useEffect, useState } from "react";
import {
    Breadcrumb,
    Layout,
    Card,
    Descriptions,
    Tag,
    Button,
    Spin,
    message,
    Row,
    Typography,
    Table,
    Space,
    Popconfirm,
    Modal,
    Form,
    Select
} from "antd";
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Link, useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { APPBACKGROUND_COLOR } from "../../appsettings";
import Headerdiv from "../../layout/header";
import { Content } from "antd/es/layout/layout";
import Footerdiv from "../../layout/footer";
import axiosClient from "../../../api/axiosClient";

const { Title } = Typography;

export const MemberView = () => {
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState([]);
    const [metricPagination, setMetricPagination] = useState({
        current_page: 1,
        per_page: 5,
        total_records: 0,
    });
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [assignedTrainers, setAssignedTrainers] = useState([]);
    const [relationships, setRelationships] = useState([]);
    const [relationshipsLoading, setRelationshipsLoading] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchMetrics = async () => {
            if (!id) return;
            setMetricsLoading(true);
            try {
                const response = await axiosClient.get(`/member/metric/get_all_member_metrics_by_id.php?member_id=${id}&page=${metricPagination.current_page}&pageSize=${metricPagination.per_page}`);
                const data = response.data;

                if (data.status === "success") {
                    setMetrics(data.data);
                    setMetricPagination({
                        current: data.page,
                        pageSize: data.pageSize,
                        total: data.total
                    });
                }
            } catch (error) {
                console.error("Fetch metrics error:", error);
                message.error(error.response?.data?.message || "Failed to fetch metrics");
            } finally {
                setMetricsLoading(false);
            }
        };

        fetchMetrics();
    }, [id]);

    useEffect(() => {
        const fetchMemberDetails = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await axiosClient.get(`/member/get_member_by_id.php?id=${id}`);
                const data = response.data;

                if (data.status === "success") {
                    setMember(data.data);
                } else {
                    message.error(data.message || "Member not found");
                    navigate("/members/view");
                }
            } catch (error) {
                console.error("Fetch error:", error);
                message.error(error.response?.data?.message || "Network error");
            } finally {
                setLoading(false);
            }
        };

        fetchMemberDetails();
    }, [id, navigate]);

    const fetchRelationships = async () => {
        if (!id) return;
        setRelationshipsLoading(true);
        try {
            const response = await axiosClient.get(`/member/get_relationships.php?member_id=${id}`);
            if (response.data.status === "success") {
                setRelationships(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching relationships:", error);
        } finally {
            setRelationshipsLoading(false);
        }
    };

    const fetchAssignedTrainers = async () => {
        if (!id) return;
        try {
            const response = await axiosClient.get(`/member/get_assigned_trainers.php?member_id=${id}`);
            if (response.data.status === "success") {
                setAssignedTrainers(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching assigned trainers:", error);
        }
    };

    // Fetch Init Data
    useEffect(() => {
        fetchRelationships();
        fetchAssignedTrainers();
    }, [id]);


    const handleDeleteRelationship = async (relationId) => {
        try {
            setLoading(true);
            const response = await axiosClient.post(`/member/delete_relationship.php`, { relation_id: relationId });
            if (response.data.status === "success") {
                message.success("Relationship deleted successfully");
                fetchRelationships();
            } else {
                message.error(response.data.message || "Failed to delete relationship");
            }
        } catch (error) {
            console.error("Error deleting relationship:", error);
            message.error(error.response?.data?.message || "Failed to delete relationship");
        } finally {
            setLoading(false);
        }
    };

    const createMarkup = (html) => {
        return { __html: html };
    };

    const metricColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => text ? dayjs(text).format("DD MMM YYYY hh:mm A") : "N/A",
        },
        {
            title: 'Weight (kg)',
            dataIndex: 'weight',
            key: 'weight',
        },
        {
            title: 'Height (cm)',
            dataIndex: 'height',
            key: 'height',
        },
        {
            title: 'BMI',
            dataIndex: 'bmi',
            key: 'bmi',
        },
        {
            title: 'Body Fat (%)',
            dataIndex: 'body_fat',
            key: 'body_fat',
        },
        {
            title: 'Muscle Mass (kg)',
            dataIndex: 'muscle_mass',
            key: 'muscle_mass',
        },
    ];

    return (
        <Spin spinning={loading} tip="Loading Details...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>
                <Headerdiv />
                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item>
                            <Link to="/members/view">Members</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Member Details</Breadcrumb.Item>
                    </Breadcrumb>

                    {member && (
                        <Card
                            title={<Title level={4} style={{ margin: 0 }}>{member.name}</Title>}
                            extra={
                                <Link to={`/members/edit/${id}`}>
                                    <Button type="primary" icon={<EditOutlined />}>
                                        Edit Member
                                    </Button>
                                </Link>
                            }
                        >
                            <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                                <Descriptions.Item label="Member ID">{member.member_id}</Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={member.status === 1 || member.status === true ? "green" : "red"}>
                                        {member.status === 1 || member.status === true ? "ACTIVE" : "INACTIVE"}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Active Till">
                                    {member.active_till ? (
                                        <span>
                                            {dayjs(member.active_till).format("DD MMM YYYY")}
                                            {dayjs(member.active_till).isBefore(dayjs(), 'day') && (
                                                <Tag color="red" style={{ marginLeft: 8 }}>EXPIRED</Tag>
                                            )}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#999' }}>Not set</span>
                                    )}
                                </Descriptions.Item>

                                <Descriptions.Item label="NIC">{member.nic}</Descriptions.Item>
                                <Descriptions.Item label="Gender">
                                    {member.gender === 'M' ? 'Male' : member.gender === 'F' ? 'Female' : 'Other'}
                                </Descriptions.Item>

                                <Descriptions.Item label="Birthday">
                                    {member.birthday ? dayjs(member.birthday).format("DD MMM YYYY") : "N/A"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Age">
                                    {member.birthday && dayjs(member.birthday).isValid() ? 
                                        dayjs().diff(dayjs(member.birthday), 'year') : "N/A"
                                    } Years
                                </Descriptions.Item>

                                <Descriptions.Item label="Contact Number">{member.contact_no}</Descriptions.Item>
                                <Descriptions.Item label="Emergency Contact">{member.emergency_contact}</Descriptions.Item>

                                <Descriptions.Item label="Address" span={2}>{member.address}</Descriptions.Item>

                                {/* Sizes Section */}
                                <Descriptions.Item label="T-Shirt Size">{member.tshirt_size || "N/A"}</Descriptions.Item>
                                <Descriptions.Item label="Trouser Size">{member.trouser_size || "N/A"}</Descriptions.Item>
                                <Descriptions.Item label="Shoe Size">{member.shoe_size || "N/A"}</Descriptions.Item>
                            </Descriptions>

                            {/* Health Conditions (Rich Text) */}
                            <div style={{ marginTop: "20px" }}>
                                <Title level={5}>Health & Medical Notes</Title>
                                <Card type="inner" style={{ backgroundColor: "#f9f9f9" }}>
                                    {member.health_conditions ? (
                                        <div dangerouslySetInnerHTML={createMarkup(member.health_conditions)} />
                                    ) : (
                                        <span style={{ color: "#999" }}>No health notes recorded.</span>
                                    )}
                                </Card>
                            </div>

                            {/* Metrics Table */}
                            <div style={{ marginTop: "20px" }}>
                                <Title level={5}>Performance Metrics</Title>
                                <Table
                                    dataSource={metrics}
                                    columns={metricColumns}
                                    rowKey="id"
                                    loading={metricsLoading}
                                    pagination={metricPagination}
                                    size="small"
                                    bordered
                                />
                            </div>

                            {/* Relationships Section */}
                            <div style={{ marginTop: "20px" }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <Title level={5} style={{ margin: 0 }}>Family & Relationships</Title>
                                </div>
                                <Table
                                    dataSource={relationships}
                                    rowKey="relation_id"
                                    size="small"
                                    bordered
                                    loading={relationshipsLoading}
                                    pagination={false}
                                    locale={{ emptyText: "No relationships recorded" }}
                                    columns={[
                                        {
                                            title: 'Member ID',
                                            dataIndex: 'member_id',
                                            key: 'member_id',
                                        },
                                        {
                                            title: 'Name',
                                            dataIndex: 'name',
                                            key: 'name',
                                            render: (text, record) => <Link to={`/members/view/${record.linked_id}`}>{text}</Link>
                                        },
                                        {
                                            title: 'Relationship',
                                            dataIndex: 'relation_type',
                                            key: 'relation_type',
                                            render: (text) => <Tag color="blue">{text.toUpperCase()}</Tag>
                                        },
                                        {
                                            title: 'Contact',
                                            dataIndex: 'contact_no',
                                            key: 'contact_no',
                                        },
                                        {
                                            title: 'Actions',
                                            key: 'actions',
                                            render: (_, record) => (
                                                <Popconfirm
                                                    title="Delete relationship?"
                                                    onConfirm={() => handleDeleteRelationship(record.relation_id)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button size="small" type="link" danger icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            )
                                        }
                                    ]}
                                />
                            </div>

                            {/* Assigned Trainers */}
                            <div style={{ marginTop: "20px" }}>
                                <Title level={5}>Assigned Trainers</Title>
                                <Table
                                    dataSource={assignedTrainers}
                                    rowKey="id"
                                    size="small"
                                    bordered
                                    pagination={false}
                                    locale={{ emptyText: "No trainers assigned" }}
                                    columns={[
                                        {
                                            title: 'Trainer ID',
                                            dataIndex: 'trainer_id',
                                            key: 'trainer_id',
                                        },
                                        {
                                            title: 'Name',
                                            dataIndex: 'name',
                                            key: 'name',
                                        },
                                        {
                                            title: 'NIC',
                                            dataIndex: 'nic',
                                            key: 'nic',
                                        },
                                        {
                                            title: 'Contact',
                                            dataIndex: 'contact_no',
                                            key: 'contact_no',
                                        },
                                    ]}
                                />
                            </div>

                            <Row justify="end" style={{ marginTop: 20 }}>
                                <Button onClick={() => navigate("/members/view")} icon={<ArrowLeftOutlined />}>
                                    Back to List
                                </Button>
                            </Row>
                        </Card>
                    )}
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>

    );
};